"use client";

import React from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { 
  CpuIcon, 
  RocketIcon, 
  CustomerSupportIcon, 
  FlashIcon 
} from "@hugeicons/core-free-icons";

const stats = [
  {
    value: "99.2%",
    label: "Lighthouse Performance",
    sub: "Average load speed and UX core values metric across all live apps",
    icon: CpuIcon,
    color: "from-blue-500/10 to-indigo-500/10 text-blue-600 dark:text-blue-400",
  },
  {
    value: "50+",
    label: "Bespoke Apps Shipped",
    sub: "Custom digital platforms launched for global startups & enterprise clients",
    icon: RocketIcon,
    color: "from-pink-500/10 to-rose-500/10 text-pink-600 dark:text-pink-400",
  },
  {
    value: "100%",
    label: "Delivery Satisfaction",
    sub: "Our clients continue to partner with us for scaling, updates and updates",
    icon: CustomerSupportIcon,
    color: "from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400",
  },
  {
    value: "24h",
    label: "Concept Turnaround",
    sub: "Initial interactive architecture sandbox delivered to review in 24 hours",
    icon: FlashIcon,
    color: "from-cyan-500/10 to-teal-500/10 text-cyan-600 dark:text-cyan-400",
  },
];

export default function WhyChooseUs() {
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 140,
        damping: 18,
      },
    },
  };

  return (
    <div className="py-24 relative px-4 flex flex-col justify-center items-center bg-neutral-50 dark:bg-black transition-colors duration-500">
      {/* Background radial accent */}
      <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] rounded-full bg-blue-650/5 dark:bg-blue-955/[0.01] blur-[150px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto text-center px-4 mb-16 z-10"
      >
        <div className="inline-block px-3 py-1 rounded-full text-xs font-mono font-bold tracking-wider text-blue-600 dark:text-blue-400 bg-blue-500/10 uppercase mb-4">
          Milestones & Impact
        </div>
        <h2 className="text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl font-recoleta tracking-tight">
          By The Numbers
        </h2>
        <p className="mt-4 text-lg sm:text-xl text-neutral-600 dark:text-neutral-400 font-manjari font-light max-w-2xl mx-auto leading-relaxed">
          We believe in objective results. Here is the operational footprint and standard performance 
          level we deliver.
        </p>
      </motion.div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="px-4 max-w-6xl w-full mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 z-10"
      >
        {stats.map((stat) => (
          <motion.div
            key={stat.label}
            variants={itemVariants}
            whileHover={{ y: -6, scale: 1.015 }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            className="cursor-pointer"
          >
            <div className="h-full border border-neutral-200/50 dark:border-neutral-800/40 bg-white/70 dark:bg-neutral-900/10 backdrop-blur-xl shadow-lg hover:shadow-xl hover:border-slate-350 dark:hover:border-neutral-700/60 transition-all duration-300 rounded-3xl p-6 flex flex-col items-center text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${stat.color} mb-4 shrink-0`}>
                <HugeiconsIcon icon={stat.icon} className="w-6 h-6" size={24} />
              </div>
              <h3 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white font-recoleta tracking-tight mb-2">
                {stat.value}
              </h3>
              <p className="text-slate-800 dark:text-slate-200 font-outfit text-sm font-bold tracking-wide mb-1">
                {stat.label}
              </p>
              <p className="text-neutral-500 dark:text-neutral-450 font-outfit text-xs leading-relaxed font-medium">
                {stat.sub}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
